import React from "react";
import type { LegalInline, LegalSection } from "./types";

const linkStyle: React.CSSProperties = {
  color: "var(--text-primary)",
  textDecoration: "underline",
  textUnderlineOffset: 2,
  textDecorationColor: "var(--border-strong)",
};

function Inline({ node }: { node: LegalInline }) {
  if (typeof node === "string") return <>{node}</>;
  if ("mail" in node) {
    return <a href={`mailto:${node.mail}`} style={linkStyle}>{node.mail}</a>;
  }
  return (
    <a href={node.href} target={node.href.startsWith("/") ? undefined : "_blank"} rel="noopener noreferrer" style={linkStyle}>
      {node.link}
    </a>
  );
}

/**
 * Shared renderer for the structured legal pages (Privacy, Impressum). Content
 * comes from the active locale's dictionary; this only lays it out.
 */
export function LegalPage({
  title,
  subtitle,
  sections,
}: {
  title: string;
  subtitle?: string;
  sections: LegalSection[];
}) {
  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 py-16 md:py-24">
      <h1
        className={`text-4xl sm:text-5xl font-semibold tracking-[-0.02em] ${subtitle ? "mb-4" : "mb-12"}`}
        style={{ color: "var(--text-primary)" }}
      >
        {title}
      </h1>
      {subtitle && (
        <p className="text-[13px] leading-[1.75] mb-12" style={{ color: "var(--text-muted)" }}>
          {subtitle}
        </p>
      )}

      <div className="space-y-10">
        {sections.map((sec, i) => (
          <div key={i}>
            {sec.title && (
              <h2 className="text-[15px] font-medium mb-3" style={{ color: "var(--text-primary)" }}>
                {sec.title}
              </h2>
            )}
            <div className="text-[13px] leading-[1.75] space-y-3" style={{ color: "var(--text-secondary)" }}>
              {sec.paragraphs.map((p, j) => (
                <p key={j} className={p.pre ? "whitespace-pre-line" : undefined}>
                  {p.nodes.map((n, k) => (
                    <Inline key={k} node={n} />
                  ))}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
